import type { CompanyWorkingHours } from '@/types/domain/company-working-hours';
import { getDayOfWeekOrder, translateDayOfWeek } from '@/types/enums/days-of-weeks';
import { addHours, isValid, parseISO } from 'date-fns';

const UTC_MINUS_THREE_HOURS = -3;
const ZULU_TIME_ONLY_REGEX = /^(\d{2}):(\d{2})(?::\d{2}(?:\.\d{1,3})?)?Z$/i;
const TIME_ONLY_REGEX = /^(\d{2}):(\d{2})(?::\d{2}(?:\.\d{1,3})?)?$/;
const EXPLICIT_TIMEZONE_REGEX = /(?:[zZ]|[+-]\d{2}:\d{2})$/;

interface WorkingHourTimeRange {
  startTime: string;
  endTime: string;
}

export interface GroupedCompanyWorkingHours {
  dayOfWeek: string;
  order: number;
  timeRanges: WorkingHourTimeRange[];
  formattedTimeRanges: string;
}

export interface CompanyOpenStatus {
  isOpen: boolean;
  label: string;
}

function convertTimeToMinutes(time: string) {
  const [hours = '0', minutes = '0'] = time.split(':');

  return Number(hours) * 60 + Number(minutes);
}

function formatUtcHoursAndMinutes(date: Date) {
  return `${String(date.getUTCHours()).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')}`;
}

function buildUtcDate(hours: number, minutes: number) {
  return new Date(Date.UTC(1970, 0, 1, hours, minutes));
}

function normalizeZuluTimeToUtcMinusThree(time: string) {
  const zuluTimeOnlyMatch = time.match(ZULU_TIME_ONLY_REGEX);

  if (zuluTimeOnlyMatch) {
    const [, hours, minutes] = zuluTimeOnlyMatch;
    const shiftedDate = addHours(buildUtcDate(Number(hours), Number(minutes)), UTC_MINUS_THREE_HOURS);

    return formatUtcHoursAndMinutes(shiftedDate);
  }

  if (time.includes('T') && EXPLICIT_TIMEZONE_REGEX.test(time)) {
    const parsedDate = parseISO(time);

    if (isValid(parsedDate)) {
      return formatUtcHoursAndMinutes(addHours(parsedDate, UTC_MINUS_THREE_HOURS));
    }
  }

  const timeOnlyMatch = time.match(TIME_ONLY_REGEX);

  if (timeOnlyMatch) {
    const [, hours, minutes] = timeOnlyMatch;

    return `${hours}:${minutes}`;
  }

  return time.slice(0, 5);
}

function getUtcMinusThreeDate(date: Date) {
  return addHours(date, UTC_MINUS_THREE_HOURS);
}

export function groupCompanyWorkingHours(workingHours?: CompanyWorkingHours[]) {
  const groupedWorkingHours = new Map<
    string,
    {
      dayOfWeek: string;
      order: number;
      timeRanges: WorkingHourTimeRange[];
    }
  >();

  for (const workingHour of workingHours ?? []) {
    const order = getDayOfWeekOrder(workingHour.dayOfWeek);
    const groupKey = order === Number.MAX_SAFE_INTEGER ? String(workingHour.dayOfWeek) : String(order);
    const existingGroup = groupedWorkingHours.get(groupKey);
    const startTime = normalizeZuluTimeToUtcMinusThree(workingHour.startTime);
    const endTime = normalizeZuluTimeToUtcMinusThree(workingHour.endTime);

    if (existingGroup) {
      existingGroup.timeRanges.push({
        startTime,
        endTime,
      });
      continue;
    }

    groupedWorkingHours.set(groupKey, {
      dayOfWeek: translateDayOfWeek(workingHour.dayOfWeek),
      order,
      timeRanges: [
        {
          startTime,
          endTime,
        },
      ],
    });
  }

  return Array.from(groupedWorkingHours.values())
    .toSorted(
      (firstGroup, secondGroup) =>
        firstGroup.order - secondGroup.order || firstGroup.dayOfWeek.localeCompare(secondGroup.dayOfWeek),
    )
    .map((group) => {
      const timeRanges = group.timeRanges.toSorted((firstRange, secondRange) =>
        firstRange.startTime.localeCompare(secondRange.startTime),
      );

      return {
        dayOfWeek: group.dayOfWeek,
        order: group.order,
        timeRanges,
        formattedTimeRanges: timeRanges.map((timeRange) => `${timeRange.startTime} às ${timeRange.endTime}`).join(', '),
      };
    });
}

export function getCurrentDayOfWeekOrder(date: Date) {
  const currentDay = getUtcMinusThreeDate(date).getUTCDay();

  return currentDay === 0 ? 6 : currentDay - 1;
}

export function getCompanyOpenStatus(
  workingHours: GroupedCompanyWorkingHours[],
  currentDateTime: Date,
): CompanyOpenStatus {
  const currentDayOfWeekOrder = getCurrentDayOfWeekOrder(currentDateTime);
  const todayWorkingHours = workingHours.find((workingHour) => workingHour.order === currentDayOfWeekOrder);

  if (!todayWorkingHours) {
    return {
      isOpen: false,
      label: 'Fechado',
    };
  }

  const utcMinusThreeDate = getUtcMinusThreeDate(currentDateTime);
  const currentTimeInMinutes = utcMinusThreeDate.getUTCHours() * 60 + utcMinusThreeDate.getUTCMinutes();
  const currentTimeRange = todayWorkingHours.timeRanges.find((timeRange) => {
    const startTimeInMinutes = convertTimeToMinutes(timeRange.startTime);
    const endTimeInMinutes = convertTimeToMinutes(timeRange.endTime);

    return currentTimeInMinutes >= startTimeInMinutes && currentTimeInMinutes < endTimeInMinutes;
  });

  if (!currentTimeRange) {
    return {
      isOpen: false,
      label: 'Fechado',
    };
  }

  return {
    isOpen: true,
    label: `Aberto até ${currentTimeRange.endTime}`,
  };
}
